import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFileSrc } from "@/hooks/useFileSrc";
import {
  MessageCircle, Search, ArrowLeft, Send, Mic, MicOff,
  Paperclip, X, CheckCheck, Volume2, Download, Users, Hash,
} from "lucide-react";
import { toast } from "sonner";

// ── Attachment renderers ──────────────────────────────────────────────────────
function MsgAudio({ path }) {
  const src = useFileSrc(path);
  const filename = path?.split("/").pop() || "audio.webm";
  return (
    <div className="flex items-center gap-2">
      <Volume2 size={16} className="text-amber-400 shrink-0" />
      {src ? (
        <>
          <audio src={src} controls className="h-8 max-w-[160px]" />
          <a href={src} download={filename} className="text-white/40 hover:text-white/80" title="Download">
            <Download size={15} />
          </a>
        </>
      ) : (
        <span className="text-sm text-white/40">Loading audio…</span>
      )}
    </div>
  );
}

function MsgImage({ path, caption }) {
  const src = useFileSrc(path);
  const filename = path?.split("/").pop() || "image";
  return (
    <div>
      {src ? (
        <div className="relative inline-block">
          <a href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt="attachment" className="max-w-[200px] rounded-lg mb-1 cursor-pointer hover:opacity-90" />
          </a>
          <a href={src} download={filename}
            className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white hover:bg-black/80"
            title="Download">
            <Download size={13} />
          </a>
        </div>
      ) : (
        <div className="w-40 h-28 rounded-lg bg-white/10 animate-pulse" />
      )}
      {caption && <p className="text-sm mt-1">{caption}</p>}
    </div>
  );
}

const CANNED_MSGS = [
  "Running about 15 mins late",
  "At pickup, waiting for loading",
  "On the way to delivery",
  "Delivered, collecting signature",
  "Traffic delay, ETA +30 mins",
  "Unable to deliver, no one home",
  "Need assistance at this address",
  "Load secured, departing now",
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-AU", { timeStyle: "short" });
}

// ── Driver List Item (admin side) ─────────────────────────────────────────────
function DriverConvItem({ driver, lastMsg, unread, isActive, onClick }) {
  const initials = driver.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "??";
  const preview = lastMsg
    ? lastMsg.attachment_type === "audio"
      ? "🎤 Audio message"
      : lastMsg.attachment_type === "image"
      ? "📷 Image"
      : lastMsg.job_ref
      ? `[${lastMsg.job_ref}] ${lastMsg.text || "Job query"}`
      : lastMsg.text || "No messages yet"
    : "No messages yet";

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 border-b border-white/5 text-left transition-colors ${
        isActive ? "bg-amber-400/10" : "hover:bg-white/[0.03]"
      }`}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 relative"
        style={{ background: "rgba(245,158,11,0.15)", border: "1.5px solid rgba(245,158,11,0.35)" }}>
        <span className="text-amber-400 font-bold text-sm">{initials}</span>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ background: "#25D366" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`font-semibold text-sm truncate ${unread ? "text-white" : "text-white/85"}`}>
            {driver.name}
          </span>
          <span className="text-[10px] text-white/35 shrink-0">{timeAgo(lastMsg?.created_at)}</span>
        </div>
        <p className={`text-xs truncate mt-0.5 ${unread ? "text-white/80" : "text-white/45"}`}>{preview}</p>
      </div>
    </button>
  );
}

// ── Shared Conversation View (driver-direct channel) ──────────────────────────
function DirectConversationView({ driverId, displayName, user, onBack, initialJobRef }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [jobRef, setJobRef] = useState(initialJobRef || null);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [showCanned, setShowCanned] = useState(false);
  const [attachPreview, setAttachPreview] = useState(null);

  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const recTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/driver-messages/${driverId}`);
      setMessages(data);
    } catch {}
  }, [driverId]);

  useEffect(() => {
    load();
    const token = localStorage.getItem("df_token");
    const wsBase = API.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/ws/driver-chat/${driverId}?token=${token || ""}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
      } catch {}
    };
    ws.onerror = () => {
      setConnected(false);
      pollRef.current = setInterval(load, 4000);
    };
    ws.onclose = () => setConnected(false);
    return () => {
      ws.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [driverId, load]);

  // Update jobRef if initialJobRef changes (when navigating from Query a Job)
  useEffect(() => {
    if (initialJobRef) setJobRef(initialJobRef);
  }, [initialJobRef]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendText = async (msg) => {
    const trimmed = (msg || text).trim();
    if (!trimmed) return;
    setSending(true);
    const ref = msg ? null : jobRef; // canned messages don't attach job_ref
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ text: trimmed, job_ref: ref }));
        setText("");
        setJobRef(null);
      } else {
        const { data } = await api.post(`/driver-messages/${driverId}`, { text: trimmed, job_ref: ref });
        setText("");
        setJobRef(null);
        setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      }
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const sendAttachment = async (file, type) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("attachment_type", type);
      if (text.trim()) fd.append("text", text.trim());
      if (jobRef) fd.append("job_ref", jobRef);
      const { data } = await api.post(`/driver-messages/${driverId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      setText("");
      setJobRef(null);
      setAttachPreview(null);
    } catch {
      toast.error("Failed to send attachment");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await sendAttachment(file, "audio");
        setRecordSeconds(0);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      let s = 0;
      recTimerRef.current = setInterval(() => {
        s += 1;
        setRecordSeconds(s);
        if (s >= 120) stopRecording();
      }, 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    clearInterval(recTimerRef.current);
    setRecording(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachPreview({ file, url: URL.createObjectURL(file), type: "image" });
    e.target.value = "";
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  // Driver name initials for header avatar
  const initials = displayName?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "??";

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0"
        style={{ background: "rgba(255,255,255,0.02)" }}>
        {onBack && (
          <button onClick={onBack} className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10" aria-label="Back">
            <ArrowLeft size={18} />
          </button>
        )}
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(245,158,11,0.15)", border: "1.5px solid rgba(245,158,11,0.35)" }}>
          <span className="text-amber-400 font-bold text-xs">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-white">{displayName}</p>
          <p className="text-xs text-white/40">Direct messages</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: connected ? "#22c55e" : "#4b5563" }} />
          <span className="text-[10px] text-white/30">{connected ? "Live" : "Polling"}</span>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <MessageCircle size={32} className="mx-auto mb-3 text-white/20" />
            <p className="text-sm text-white/30">No messages yet. Start the conversation!</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <span className="text-[10px] text-white/30 mb-1 px-1">
                {m.sender_name} · {m.role} · {fmtTime(m.created_at)}
              </span>
              <div className={`px-3.5 py-2.5 max-w-[80%] break-words ${mine ? "chat-bubble-mine" : "chat-bubble-theirs"}`}>
                {m.job_ref && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold mb-1.5 mr-1"
                    style={{ background: "rgba(245,158,11,0.2)", color: "#f59e0b" }}>
                    <Hash size={9} />
                    {m.job_ref.replace(/^#/, "")}
                  </span>
                )}
                {m.attachment_type === "audio" ? (
                  <MsgAudio path={m.attachment_url} />
                ) : m.attachment_type === "image" ? (
                  <MsgImage path={m.attachment_url} caption={m.text} />
                ) : (
                  <p className="text-sm leading-snug">{m.text}</p>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* ── Job context banner (driver: when querying a specific job) ── */}
      {jobRef && (
        <div className="px-4 py-2 border-t border-amber-500/20 flex items-center gap-2 shrink-0"
          style={{ background: "rgba(245,158,11,0.07)" }}>
          <Hash size={13} className="text-amber-400 shrink-0" />
          <span className="text-xs text-amber-300 flex-1">Querying job: <strong>{jobRef}</strong></span>
          <button onClick={() => setJobRef(null)} className="text-white/40 hover:text-white/80 p-1">
            <X size={13} />
          </button>
        </div>
      )}

      {/* ── Attachment preview ── */}
      {attachPreview && (
        <div className="px-4 py-2 border-t border-white/10 flex items-center gap-3 shrink-0">
          <img src={attachPreview.url} alt="preview" className="h-14 w-14 object-cover rounded-lg" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white/60 truncate">{attachPreview.file.name}</p>
            <p className="text-[10px] text-white/30">Image attachment</p>
          </div>
          <button onClick={() => setAttachPreview(null)} className="p-1 text-white/40 hover:text-white"><X size={16} /></button>
          <button onClick={() => sendAttachment(attachPreview.file, "image")} disabled={sending}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: "linear-gradient(90deg,#2b5fff,#3b82f6)" }}>
            Send
          </button>
        </div>
      )}

      {/* ── Canned Messages ── */}
      {showCanned && (
        <div className="border-t border-white/10 bg-black/20 max-h-44 overflow-y-auto shrink-0">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-white/50 uppercase tracking-widest">Quick Messages</span>
            <button onClick={() => setShowCanned(false)} className="text-white/40 hover:text-white"><X size={14} /></button>
          </div>
          {CANNED_MSGS.map((msg) => (
            <button key={msg}
              className="w-full text-left px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.04] border-b border-white/5 last:border-0"
              onClick={() => { sendText(msg); setShowCanned(false); }}>
              {msg}
            </button>
          ))}
        </div>
      )}

      {/* ── Input Row ── */}
      <div className="px-3 py-3 border-t border-white/10 flex items-end gap-2 shrink-0">
        <div className="flex flex-col gap-1">
          <button onClick={() => setShowCanned((s) => !s)}
            className={`p-2 rounded-xl transition-colors ${showCanned ? "bg-amber-400/20 text-amber-400" : "text-white/40 hover:text-white/70 hover:bg-white/10"}`}
            title="Quick messages">
            <CheckCheck size={18} />
          </button>
          <button onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/10 transition-colors"
            title="Attach image">
            <Paperclip size={18} />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileChange} />
        </div>
        <div className="flex-1 relative">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey}
            placeholder={jobRef ? `Message about ${jobRef}…` : "Send a message…"}
            rows={1}
            className="w-full bg-white/5 border border-white/15 rounded-2xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none leading-snug"
            style={{ maxHeight: "100px" }} />
        </div>
        {text.trim() || attachPreview ? (
          <button onClick={() => sendText()} disabled={sending}
            className="p-3 rounded-2xl text-white disabled:opacity-40 shrink-0"
            style={{ background: "linear-gradient(135deg,#2b5fff,#3b82f6)" }}>
            <Send size={18} />
          </button>
        ) : recording ? (
          <button onClick={stopRecording}
            className="p-3 rounded-2xl text-white shrink-0 recording-btn"
            style={{ background: "#ef4444" }}
            title={`Stop recording (${recordSeconds}s)`}>
            <MicOff size={18} />
          </button>
        ) : (
          <button onClick={startRecording}
            className="p-3 rounded-2xl text-white/60 hover:text-white hover:bg-white/10 shrink-0 transition-colors"
            title="Record voice message">
            <Mic size={18} />
          </button>
        )}
      </div>
      {recording && (
        <div className="flex items-center justify-center gap-2 py-1.5 bg-red-500/10 border-t border-red-500/20 shrink-0">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-xs text-red-400 font-semibold">Recording… {recordSeconds}s</span>
        </div>
      )}
    </div>
  );
}

// ── Admin: Driver Messages View ───────────────────────────────────────────────
function AdminDriversMessages({ user }) {
  const [drivers, setDrivers] = useState([]);
  const [conversations, setConversations] = useState({});
  const [activeDriverId, setActiveDriverId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const activeDriver = drivers.find((d) => d.id === activeDriverId) || null;

  useEffect(() => {
    (async () => {
      try {
        const { data: driverList } = await api.get("/drivers");
        setDrivers(driverList);
        const convData = {};
        await Promise.allSettled(
          driverList.map(async (d) => {
            try {
              const { data: msgs } = await api.get(`/driver-messages/${d.id}`);
              const stored = localStorage.getItem(`driver_msg_lastseen_${d.id}`);
              let seenIdx = -1;
              if (stored && msgs.length) seenIdx = msgs.findIndex((m) => m.id === stored);
              const unread = msgs.slice(seenIdx + 1).filter((m) => m.role !== "admin").length;
              convData[d.id] = { lastMsg: msgs[msgs.length - 1] || null, unread };
            } catch {
              convData[d.id] = { lastMsg: null, unread: 0 };
            }
          })
        );
        setConversations(convData);
      } catch {
        toast.error("Failed to load conversations");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const openConv = (driverId) => {
    setActiveDriverId(driverId);
    setConversations((c) => ({ ...c, [driverId]: { ...(c[driverId] || {}), unread: 0 } }));
    api.get(`/driver-messages/${driverId}`).then(({ data: msgs }) => {
      if (msgs.length) localStorage.setItem(`driver_msg_lastseen_${driverId}`, msgs[msgs.length - 1].id);
    }).catch(() => {});
  };

  const filteredDrivers = [...drivers]
    .filter((d) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return d.name?.toLowerCase().includes(q) || d.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const ta = conversations[a.id]?.lastMsg?.created_at || "";
      const tb = conversations[b.id]?.lastMsg?.created_at || "";
      return tb.localeCompare(ta);
    });

  const totalUnread = Object.values(conversations).reduce((s, c) => s + (c?.unread || 0), 0);

  return (
    <div className="h-[calc(100svh-200px)] lg:h-[calc(100svh-140px)] min-h-[400px] flex rounded-2xl overflow-hidden border border-white/10"
      style={{ background: "rgba(255,255,255,0.015)" }}>

      {/* ── Driver List ── */}
      <div className={`flex flex-col border-r border-white/10 ${activeDriverId ? "hidden md:flex" : "flex"} w-full md:w-[300px] shrink-0`}>
        <div className="px-4 pt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="brand-title text-xl text-white/90">Messages</h2>
            {totalUnread > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: "#25D366" }}>
                {totalUnread}
              </span>
            )}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search drivers…"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/25" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="py-12 text-center text-white/30 text-sm">Loading…</div>}
          {!loading && filteredDrivers.length === 0 && (
            <div className="py-12 text-center">
              <Users size={32} className="mx-auto mb-3 text-white/15" />
              <p className="text-sm text-white/30">No drivers found</p>
            </div>
          )}
          {filteredDrivers.map((d) => (
            <DriverConvItem
              key={d.id}
              driver={d}
              lastMsg={conversations[d.id]?.lastMsg || null}
              unread={conversations[d.id]?.unread || 0}
              isActive={d.id === activeDriverId}
              onClick={() => openConv(d.id)}
            />
          ))}
        </div>
      </div>

      {/* ── Chat Area ── */}
      <div className={`flex-1 flex flex-col ${activeDriverId ? "flex" : "hidden md:flex"}`}>
        {!activeDriver ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <Users size={48} className="text-white/10 mb-4" />
            <p className="text-white/40 text-sm">Select a driver to start messaging</p>
            <p className="text-white/20 text-xs mt-1">Each driver has a dedicated chat channel</p>
          </div>
        ) : (
          <DirectConversationView
            key={activeDriver.id}
            driverId={activeDriver.id}
            displayName={activeDriver.name}
            user={user}
            onBack={() => setActiveDriverId(null)}
          />
        )}
      </div>
    </div>
  );
}

// ── Driver: Single Messages View ──────────────────────────────────────────────
function DriverMessagesView({ user, initialJobRef }) {
  return (
    <>
      <h1 className="brand-title text-3xl mb-1">Messages</h1>
      <p className="text-white/60 mb-4">Chat with DRIVN Dispatch about your runs.</p>
      <div className="h-[calc(100svh-260px)] min-h-[360px] rounded-2xl overflow-hidden border border-white/10"
        style={{ background: "rgba(255,255,255,0.015)" }}>
        <DirectConversationView
          driverId={user.id}
          displayName="DRIVN Dispatch"
          user={user}
          onBack={null}
          initialJobRef={initialJobRef}
        />
      </div>
    </>
  );
}

// ── Main Messages Page ────────────────────────────────────────────────────────
export default function Messages() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  if (!user) return null;

  if (user.role === "driver") {
    return <DriverMessagesView user={user} initialJobRef={searchParams.get("jobRef")} />;
  }

  if (user.role === "admin") {
    return <AdminDriversMessages user={user} />;
  }

  return null;
}
